import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine
} from 'recharts';

const getCurrentISTHour = () => {
  const ist = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return ist.getHours();
};

const generateTargetPace = (totalTarget) => {
  return Array.from({ length: 17 }, (_, i) => ({
    hour: i + 6,
    label: `${i + 6}:00`,
    target: Math.round((totalTarget / 16) * i),
  }));
};

const formatYAxis = (v) =>
  v >= 100000 ? `\u20B9${(v / 100000).toFixed(1)}L` : `\u20B9${(v / 1000).toFixed(0)}K`;

const formatTooltipValue = (value) => {
  if (value >= 100000) return `\u20B9${(value / 100000).toFixed(2)}L`;
  if (value >= 1000) return `\u20B9${(value / 1000).toFixed(1)}K`;
  return `\u20B9${value}`;
};

// Map revenue into time slots based on current IST hour
// Q1 (6-9): 3hrs, Q2 (9-12): 3hrs, Q3 (12-15): 3hrs, Q4 (15-18): 3hrs, Q5 (18-22): 4hrs
const buildActualRevenueLine = (revenueDone, totalTarget) => {
  const currentHour = getCurrentISTHour();
  const slots = [
    { start: 6, end: 9, hours: 3 },
    { start: 9, end: 12, hours: 3 },
    { start: 12, end: 15, hours: 3 },
    { start: 15, end: 18, hours: 3 },
    { start: 18, end: 22, hours: 4 },
  ];

  // Figure out how many total working hours have elapsed (6am baseline)
  const clampedHour = Math.max(6, Math.min(22, currentHour));
  const elapsedHours = clampedHour - 6;
  if (elapsedHours <= 0) return [];

  // Distribute revenue proportionally across elapsed slots
  const points = [];
  let cumulativeRevenue = 0;

  for (const slot of slots) {
    if (slot.start >= clampedHour) break;
    const slotEnd = Math.min(slot.end, clampedHour);
    const slotHoursElapsed = slotEnd - slot.start;
    const slotFraction = slotHoursElapsed / elapsedHours;
    const slotRevenue = Math.round(revenueDone * slotFraction);

    // Add data points for each hour in this slot
    for (let h = slot.start; h <= slotEnd; h++) {
      if (h > clampedHour) break;
      const hourFraction = (h - slot.start) / slotHoursElapsed;
      points.push({
        hour: h,
        actual: Math.round(cumulativeRevenue + slotRevenue * hourFraction),
      });
    }
    cumulativeRevenue += slotRevenue;
  }

  return points;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#1a1a2e',
      border: '1px solid #333',
      borderRadius: 8,
      padding: '10px 14px',
      fontSize: 13,
    }}>
      <p style={{ color: '#ccc', marginBottom: 6, fontWeight: 600 }}>{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
          {entry.name}: {formatTooltipValue(entry.value)}
        </p>
      ))}
    </div>
  );
};

export default function RevenueTimelineChart({ totalTarget, revenueDone, selectedDate, clusters }) {
  const currentHour = getCurrentISTHour();

  const chartData = useMemo(() => {
    const paceData = generateTargetPace(totalTarget || 0);
    const actualPoints = buildActualRevenueLine(revenueDone || 0, totalTarget || 0);

    // Merge actual revenue into pace data
    const actualMap = {};
    actualPoints.forEach(p => { actualMap[p.hour] = p.actual; });

    return paceData.map(p => ({
      ...p,
      actual: actualMap[p.hour] !== undefined ? actualMap[p.hour] : null,
    }));
  }, [totalTarget, revenueDone]);

  const isAhead = (revenueDone || 0) >= (totalTarget || 0) * ((currentHour - 6) / 16);

  return (
    <div style={{
      background: '#1a1a2e',
      borderRadius: 12,
      padding: '20px',
      border: '1px solid rgba(255,255,255,0.1)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 600, margin: 0 }}>
          Revenue vs Target Pace
        </h3>
        <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#8884d8' }}>
            <span style={{
              width: 20, height: 2, background: '#8884d8',
              display: 'inline-block', borderTop: '2px dashed #8884d8',
            }} />
            Target Pace
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#22c55e' }}>
            <span style={{
              width: 20, height: 2, background: '#22c55e',
              display: 'inline-block',
            }} />
            Actual Revenue
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis
            dataKey="label"
            stroke="#666"
            tick={{ fill: '#999', fontSize: 11 }}
            interval={1}
          />
          <YAxis
            stroke="#666"
            tick={{ fill: '#999', fontSize: 11 }}
            tickFormatter={formatYAxis}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Area fill between lines — light red when behind, light green when ahead */}
          <Area
            type="monotone"
            dataKey="actual"
            fill={isAhead ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}
            stroke="none"
            connectNulls={false}
          />

          {/* Target pace line - dashed blue */}
          <Line
            type="monotone"
            dataKey="target"
            stroke="#8884d8"
            strokeWidth={2}
            strokeDasharray="6 3"
            dot={false}
            name="Target Pace"
          />

          {/* Actual revenue line - solid green */}
          <Line
            type="stepAfter"
            dataKey="actual"
            stroke="#22c55e"
            strokeWidth={2.5}
            dot={{ fill: '#22c55e', r: 3 }}
            name="Actual Revenue"
            connectNulls={false}
          />

          {/* Current time reference line */}
          {currentHour >= 6 && currentHour <= 22 && (
            <ReferenceLine
              x={`${currentHour}:00`}
              stroke="#f59e0b"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              label={{
                value: 'Now',
                position: 'top',
                fill: '#f59e0b',
                fontSize: 11,
              }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
